/**
 * UK postcode utilities.
 *
 * Postcode lookups are performed against postcodes.io
 * (a free, no-auth public API maintained by the Open Postcode Geo project).
 *
 * Supported inputs:
 *   - Full postcodes:       "E1 6AN", "SW1A 1AA", "hp11 1xy"
 *   - Outward codes only:   "E1", "SW1A", "HP11"
 *
 * Results are cached in-memory and (when available) in localStorage so
 * repeated lookups during a session do not hit the network.
 */

export type Coords = { lat: number; lng: number };

const POSTCODES_IO = 'https://api.postcodes.io';
const STORAGE_KEY = 'ct_postcode_coords_v1';
const STORAGE_NEGATIVE_KEY = 'ct_postcode_coords_negative_v1';

// UK postcode regex: covers full postcodes (e.g. "EC1A 1BB", "M1 1AE",
// "HP11 1XY") and outward-only codes (e.g. "EC1A", "M1", "HP11", "HP7").
// The third character of the second alternation is optional so that
// 2-letter + 1-digit outward codes (HP7, AB1, BA1, ...) are accepted.
// See: https://en.wikipedia.org/wiki/Postcodes_in_the_United_Kingdom
const UK_POSTCODE_REGEX =
  /^(GIR ?0AA|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y])?)|[0-9][A-HJKPSTUW])( ?[0-9][ABD-HJLNP-UW-Z]{2})?)$/i;

/**
 * Returns true if the value looks like a valid UK postcode or outward code.
 */
export function isValidUkPostcode(value: string): boolean {
  if (!value) return false;
  return UK_POSTCODE_REGEX.test(value.trim().toUpperCase());
}

/**
 * Normalise a postcode for lookup / display:
 *   "  e1 6an "  -> "E1 6AN"
 *   "e16an"      -> "E1 6AN"
 *   "e1"         -> "E1"
 */
export function normaliseUkPostcode(value: string): string {
  if (!value) return '';
  const cleaned = value.replace(/\s+/g, '').toUpperCase();
  // Full postcodes: insert a space before the final 3 chars (the "inward" part).
  if (cleaned.length > 4) {
    return `${cleaned.slice(0, cleaned.length - 3)} ${cleaned.slice(-3)}`;
  }
  return cleaned;
}

/**
 * Extract just the outward code (the part before the space).
 *   "E1 6AN" -> "E1"
 *   "HP11"   -> "HP11"
 */
export function getOutwardCode(value: string): string {
  return normaliseUkPostcode(value).split(' ')[0] ?? '';
}

// --- caching -----------------------------------------------------------------

const memoryCache = new Map<string, Coords>();
const negativeCache = new Set<string>();
let storageHydrated = false;

function hydrateFromStorage(): void {
  if (storageHydrated || typeof window === 'undefined') {
    storageHydrated = true;
    return;
  }
  storageHydrated = true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, Coords>;
      for (const [key, value] of Object.entries(parsed)) {
        memoryCache.set(key, value);
      }
    }
    const rawNeg = window.localStorage.getItem(STORAGE_NEGATIVE_KEY);
    if (rawNeg) {
      const parsed = JSON.parse(rawNeg) as string[];
      for (const key of parsed) negativeCache.add(key);
    }
  } catch {
    // Ignore: cache is best-effort.
  }
}

function persistCache(): void {
  if (typeof window === 'undefined') return;
  try {
    const obj: Record<string, Coords> = {};
    for (const [key, value] of memoryCache.entries()) obj[key] = value;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    window.localStorage.setItem(STORAGE_NEGATIVE_KEY, JSON.stringify([...negativeCache]));
  } catch {
    // Ignore quota / privacy-mode errors.
  }
}

// --- network ----------------------------------------------------------------

type PostcodesIoResponse = {
  result?: {
    latitude?: number;
    longitude?: number;
  } | null;
};

async function fetchJson(url: string): Promise<PostcodesIoResponse | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await res.json()) as PostcodesIoResponse;
  } catch {
    return null;
  }
}

function extractCoords(data: PostcodesIoResponse | null): Coords | null {
  const result = data?.result;
  if (!result || typeof result.latitude !== 'number' || typeof result.longitude !== 'number') {
    return null;
  }
  return { lat: result.latitude, lng: result.longitude };
}

async function lookupOutcode(outcode: string): Promise<Coords | null> {
  return extractCoords(
    await fetchJson(`${POSTCODES_IO}/outcodes/${encodeURIComponent(outcode)}`)
  );
}

async function lookupFullPostcode(postcode: string): Promise<Coords | null> {
  return extractCoords(
    await fetchJson(`${POSTCODES_IO}/postcodes/${encodeURIComponent(postcode)}`)
  );
}

/**
 * Look up the coordinates of a UK postcode.
 *
 * Returns null when the postcode is not recognised by postcodes.io
 * (or when the network is unavailable).
 */
export async function getPostcodeCoords(postcode: string): Promise<Coords | null> {
  hydrateFromStorage();

  const trimmed = (postcode ?? '').trim();
  if (!trimmed) return null;

  const key = normaliseUkPostcode(trimmed);
  if (!key || !isValidUkPostcode(key)) return null;

  if (memoryCache.has(key)) return memoryCache.get(key)!;
  if (negativeCache.has(key)) return null;

  const isFull = key.includes(' ');
  // Try the most specific endpoint first, then fall back to the outward code.
  let coords: Coords | null = null;
  if (isFull) {
    coords = await lookupFullPostcode(key);
    if (!coords) {
      const outward = getOutwardCode(key);
      if (outward && outward !== key) {
        coords = await lookupOutcode(outward);
      }
    }
  } else {
    coords = await lookupOutcode(key);
  }

  if (coords) {
    memoryCache.set(key, coords);
    persistCache();
    return coords;
  }

  negativeCache.add(key);
  persistCache();
  return null;
}

// --- distance ---------------------------------------------------------------

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Haversine great-circle distance in km between two coordinates.
 */
export function haversineDistanceKm(a: Coords, b: Coords): number {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinDLng * sinDLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Distance in km between two postcodes. Returns null if either postcode
 * cannot be resolved.
 */
export async function getPostcodeDistance(
  postcodeA: string,
  postcodeB: string
): Promise<number | null> {
  const [a, b] = await Promise.all([getPostcodeCoords(postcodeA), getPostcodeCoords(postcodeB)]);
  if (!a || !b) return null;
  return haversineDistanceKm(a, b);
}

/**
 * Synchronous distance helper used when the caller has already resolved
 * coordinates for both postcodes (e.g. via a react-query hook).
 */
export function distanceFromCoords(a: Coords | null | undefined, b: Coords | null | undefined): number | null {
  if (!a || !b) return null;
  return haversineDistanceKm(a, b);
}
